/**-----------------------------------------------------------------------------------------
* Copyright © 2023 Progress Software Corporation. All rights reserved.
* Licensed under commercial license. See LICENSE.md in the package root for more information
*-------------------------------------------------------------------------------------------*/
"use client";
import * as p from "react";
import t from "prop-types";
import { saveAs as g } from "@progress/kendo-file-saver";
import { ExcelExporter as f, Workbook as m } from "@progress/kendo-ooxml";
import * as H from "@progress/kendo-ooxml";
import * as C from "react-dom/server";
import { validatePackage as x } from "@progress/kendo-react-common";
const d = (r, e, o) => (i) => {
  o(e, i);
  const n = C.renderToStaticMarkup(/* @__PURE__ */ p.createElement(r, { ...e })), l = Number(n);
  return isNaN(l) ? n : l;
}, y = (r, e) => {
  r.$implicit = r.group = e, r.field = e.field, r.value = e.value, r.aggregates = e.aggregates;
}, O = (r, e) => {
  r.group = e.group, r.$implicit = r.aggregates = e;
}, b = (r, e) => {
}, k = (r) => {
  const e = [];
  let o = 0;
  const s = (i, n, l) => {
    i.forEach((a) => {
      if (a.level === l) {
        const h = new E(a, o);
        if (n.push(h), a.children && a.children.length > 1) {
          const u = h.columns = [];
          s(a.children, u, l + 1);
        } else
          o++;
      }
    });
  };
  return s(r, e, 0), e;
};
class E {
  constructor(e, o) {
    this.columns = null, this.title = e.title, this.field = e.field, this.hidden = e.hidden, this.locked = e.locked, this.width = e.width, this.headerCellOptions = e.headerCellOptions, this.cellOptions = e.cellOptions, this.groupHeaderCellOptions = e.groupHeaderCellOptions, this.groupFooterCellOptions = e.groupFooterCellOptions, this.footerCellOptions = e.footerCellOptions, e.footer && (this.footerTemplate = d(e.footer, {
      $implicit: e,
      column: e,
      columnIndex: o
    }, b)), e.groupFooter && (this.groupFooterTemplate = d(e.groupFooter, {
      column: e,
      field: e.field
    }, O)), e.groupHeader && (this.groupHeaderTemplate = d(e.groupHeader, {}, y));
  }
}
const w = (r) => Math.max(...r.map((e) => e.level)) + 1, P = (r) => {
  const e = k(r.columns), s = new f({
    columns: e,
    data: r.data,
    filterable: r.filterable,
    groups: r.group,
    paddingCellOptions: r.paddingCellOptions,
    headerPaddingCellOptions: r.headerPaddingCellOptions,
    hierarchy: r.hierarchy ? { depth: w(r.data), itemLevel: (i) => i.level } : null,
    collapsible: r.collapsible
  }).workbook();
  return s.creator = r.creator, s.date = r.date, s.rtl = r.dir === "rtl", s;
}, R = (r) => new m(r).toDataURL(), T = (r) => r && r.sheets, F = {
  name: "@progress/kendo-react-excel-export",
  productName: "KendoReact",
  productCodes: ["KENDOUIREACT", "KENDOUICOMPLETE"],
  publishDate: 1702645202,
  version: "",
  licensingDocsUrl: "https://www.telerik.com/kendo-react-ui/components/my-license/"
};
class c extends p.Component {
  constructor(e) {
    super(e), this.saveFile = (o) => {
      g(o, this.props.fileName, {
        forceProxy: this.props.forceProxy,
        proxyURL: this.props.proxyURL
      });
    }, this.extractColumns = (o, s = 0) => Array.isArray(o) ? o.map((i) => this.extractChild(i, s)) : [o, this.extractChild(o, s)], this.extractChild = (o, s = 0) => p.isValidElement(o) ? {
      ...o.props,
      width: o.props.width && parseInt(`${o.props.width}`, 10),
      level: s,
      children: o.props.children && this.extractColumns(o.props.children, s + 1)
    } : {
      ...o,
      level: s,
      children: o.children && this.extractColumns(o.children, s + 1)
    }, this.getExportData = (o) => {
      let s;
      return o ? Array.isArray(o) ? s = {
        data: o
      } : s = o : s = {
        data: this.props.data,
        group: this.props.group
      }, s;
    }, x(F), this.save = this.save.bind(this), this.toDataURL = this.toDataURL.bind(this), this.workbookOptions = this.workbookOptions.bind(this);
  }
  /**
   * Saves the data to Excel.
   *
   * @param exportData - An optional parameter. Can be the data that will be exported or the [`WorkbookOptions`]({% slug api_excel-export_workbookoptions %}).
   * @param columns - An optional parameter. If present, it will be used instead of the columns prop or the child column components.
   */
  save(e, o) {
    this.toDataURL(e, o).then((...s) => {
      this.props.onExportComplete && this.props.onExportComplete.call(void 0, { target: this }), this.saveFile(...s);
    });
  }
  /**
   * Returns a promise which will be resolved with the file data URI.
   *
   * @param exportData - The optional data or the [`WorkbookOptions`]({% slug api_excel-export_workbookoptions %}) that will be used to generate the data URI.
   * @param externalColumns - The optional columns that will be used.
   * @returns {Promise<string>} - The promise that will be resolved by the file data URI.
   */
  toDataURL(e, o) {
    const s = T(e) ? e : this.workbookOptions(e, o);
    return R(s);
  }
  /**
   * Based on the specified columns and data, returns [`WorkbookOptions`]({% slug api_excel-export_workbookoptions %}).
   *
   * @param exportData - The optional data that will be exported.
   * @param externalColumns - The optional columns that will be used.
   * @returns {WorkbookOptions} - The workbook options.
   */
  // tslint:enable:max-line-length
  workbookOptions(e, o) {
    const s = this.getExportData(e), i = this.props.children, n = i && i.type && i.type.displayName === "KendoReactGrid" && i.props && p.Children.toArray(i.props.children), l = this.extractColumns(
      n || o || this.props.columns || p.Children.toArray(i)
    );
    return P({
      columns: l,
      data: s.data,
      group: s.group,
      filterable: this.props.filterable,
      creator: this.props.creator,
      date: this.props.date,
      dir: this.props.dir,
      hierarchy: this.props.hierarchy,
      paddingCellOptions: this.props.paddingCellOptions,
      headerPaddingCellOptions: this.props.headerPaddingCellOptions,
      collapsible: this.props.collapsible
    });
  }
  /**
   * @hidden
   */
  render() {
    return this.props.children || null;
  }
}
c.propTypes = {
  children: t.any,
  columns: t.arrayOf(t.any),
  creator: t.string,
  data: t.any,
  date: t.any,
  filterable: t.bool,
  fileName: t.string,
  forceProxy: t.bool,
  group: t.any,
  headerPaddingCellOptions: t.any,
  paddingCellOptions: t.any,
  proxyURL: t.string,
  dir: t.string,
  hierarchy: t.bool,
  collapsible: t.bool
};
c.defaultProps = {
  fileName: "Export.xlsx",
  forceProxy: !1,
  collapsible: !1
};
const v = (r) => null;
v.propTypes = {
  cellOptions: t.any,
  field: t.string,
  footerCellOptions: t.any,
  footer: t.oneOfType([t.func, t.element]),
  groupFooterCellOptions: t.any,
  groupFooter: t.oneOfType([t.func, t.element]),
  groupHeaderCellOptions: t.any,
  groupHeader: t.oneOfType([t.func, t.element]),
  headerCellOptions: t.any,
  hidden: t.bool,
  level: t.number,
  locked: t.bool,
  title: t.string,
  width: t.number
};
const U = (r) => null;
U.propTypes = {
  children: t.oneOfType([t.arrayOf(t.element), t.element]),
  headerCellOptions: t.any,
  hidden: t.bool,
  level: t.number,
  locked: t.bool,
  title: t.string,
  width: t.number
};
class G extends p.PureComponent {
}
class K extends p.PureComponent {
}
class M extends p.PureComponent {
}
export {
  c as ExcelExport,
  v as ExcelExportColumn,
  U as ExcelExportColumnGroup,
  G as ExcelExportFooter,
  K as ExcelExportGroupFooter,
  M as ExcelExportGroupHeader,
  H as KendoOoxml,
  T as isWorkbookOptions,
  R as toDataURL,
  P as workbookOptions
};
